import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { DraftsPanel } from '../components/DraftsPanel';
import { UsageMeter } from '../components/UsageMeter';
import { StarterUsageCounter } from '../components/StarterUsageCounter';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { UpgradePromptModal } from '../components/UpgradePromptModal';
import { EmailVerificationBanner } from '../components/EmailVerificationBanner';
import { NPSSurveyModal } from '../components/NPSSurveyModal';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Plus, Users, FileText, TrendingUp, ClipboardCheck, TrendingUp as EvolutionIcon } from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradePromptOpen, setUpgradePromptOpen] = useState(false);
  const [npsOpen, setNpsOpen] = useState(false);
  const { isDark } = useTheme();
  const { user, refreshUser } = useAuth();
  const isStarter = user?.plan_type === 'starter';

  useEffect(() => {
    loadStats();
    // ONBOARD-01 + NPS-01 — pull fresh user state on mount, then decide
    // whether to surface the NPS modal. Doing the check AFTER refreshUser
    // resolves protects against a stale `nps.survey_shown` in AuthContext.
    (async () => {
      let fresh = null;
      try { fresh = await refreshUser?.(); } catch { /* best-effort */ }
      try {
        const pending = localStorage.getItem('pending_nps') === 'true';
        const nps = (fresh || user)?.nps;
        const alreadyShown = Boolean(
          nps?.survey_shown || nps?.responded_at || nps?.dismissed_at
        );
        if (pending && !alreadyShown) {
          setNpsOpen(true);
          localStorage.removeItem('pending_nps');
        }
      } catch { /* no localStorage — safe to ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ONBOARD-01 — open the 12/15 upgrade modal once, when:
  //   * user is on Starter plan,
  //   * consultations_used >= 12 (from /usage endpoint),
  //   * onboarding.upgrade_prompt_12_shown is still false.
  // Dismissal flips the flag server-side so we never re-prompt.
  useEffect(() => {
    if (!user || !isStarter) return;
    if (user.onboarding?.upgrade_prompt_12_shown) return;
    let mounted = true;
    (async () => {
      try {
        const u = await api.getUsageStats();
        if (!mounted) return;
        if ((u?.consultations_used || 0) >= 12) {
          setUpgradePromptOpen(true);
        }
      } catch {
        // best-effort — never break dashboard load
      }
    })();
    return () => { mounted = false; };
  }, [user, isStarter]);

  const loadStats = async () => {
    try {
      const data = await api.getDashboardStats();
      setStats(data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { 
        label: '📝 Rascunho', 
        className: 'bg-gray-100 text-gray-700 dark:bg-[#1F2937] dark:text-[#9CA3AF]' 
      },
      in_review: { 
        label: '⏳ Em Revisão', 
        className: 'bg-orange-100 text-orange-700 dark:bg-[#78350F] dark:text-[#FBBF24]' 
      },
      approved: { 
        label: '✓ Aprovado', 
        className: 'bg-green-100 text-green-700 dark:bg-[#14532D] dark:text-[#4ADE80]' 
      },
      exported: { 
        label: '✓ Exportado', 
        className: 'bg-green-100 text-green-700 dark:bg-[#14532D] dark:text-[#4ADE80]' 
      }
    };
    return badges[status] || badges.draft;
  };

  const getTypeBadge = (consultationType) => {
    if (consultationType === 'evolucao') {
      return {
        label: 'Evolução',
        className: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
      };
    }
    return {
      label: 'Avaliação',
      className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return { date: '--/--/----', time: '--:--' };
    try {
      const date = new Date(dateString);
      return {
        date: format(date, 'dd/MM/yyyy'),
        time: format(date, 'HH:mm')
      };
    } catch {
      return { date: '--/--/----', time: '--:--' };
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-8">
          <div>
            <h1 className={`text-2xl sm:text-3xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>
              Dashboard
            </h1>
            <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
              Visão geral das suas atividades
            </p>
          </div>
          <Link to="/consultations/new">
            <Button className="w-full sm:w-auto bg-primary hover:bg-primary-hover" data-testid="new-consultation-btn">
              <Plus className="h-5 w-5 mr-2" />
              Nova Consulta
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          </div>
        ) : (
          <>
            {/* AUTH-02 — Email verification banner (only when email_verified === false) */}
            <EmailVerificationBanner />

            {/* ONBOARD-01 — First Wow milestones (hidden when completed_at set) */}
            <OnboardingChecklist />

            {/* Usage meter — Starter gets the activation-focused counter,
                Solo/Clínica keep the generic burn-down meter (BILLING-02). */}
            <div className="mb-6">
              {isStarter ? <StarterUsageCounter /> : <UsageMeter />}
            </div>

            {/* DRAFT-01 — Rascunhos em Andamento (only renders if user has any) */}
            <DraftsPanel />

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className={`${isDark ? 'bg-[#1A1D27] border-[#2A2E42]' : 'bg-white border-slate-200'} p-6 rounded-xl border shadow-sm hover:shadow-md transition-shadow`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <span className={`text-xs font-medium ${isDark ? 'text-[#9BA3BF]' : 'text-slate-500'} uppercase tracking-wide`}>Pacientes</span>
                </div>
                <div className={`text-3xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-1`}>
                  {stats?.active_patients || 0}
                </div>
                <p className={`text-sm ${isDark ? 'text-[#9BA3BF]' : 'text-slate-600'}`}>Pacientes ativos</p>
              </div>

              <div className={`${isDark ? 'bg-[#1A1D27] border-[#2A2E42]' : 'bg-white border-slate-200'} p-6 rounded-xl border shadow-sm hover:shadow-md transition-shadow`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-secondary-light rounded-lg flex items-center justify-center">
                    <FileText className="h-6 w-6 text-secondary" />
                  </div>
                  <span className={`text-xs font-medium ${isDark ? 'text-[#9BA3BF]' : 'text-slate-500'} uppercase tracking-wide`}>Consultas</span>
                </div>
                <div className={`text-3xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-1`}>
                  {stats?.total_consultations || 0}
                </div>
                <p className={`text-sm ${isDark ? 'text-[#9BA3BF]' : 'text-slate-600'}`}>Total de consultas</p>
              </div>

              <div className={`${isDark ? 'bg-[#1A1D27] border-[#2A2E42]' : 'bg-white border-slate-200'} p-6 rounded-xl border shadow-sm hover:shadow-md transition-shadow`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-primary" />
                  </div>
                  <span className={`text-xs font-medium ${isDark ? 'text-[#9BA3BF]' : 'text-slate-500'} uppercase tracking-wide`}>Plano</span>
                </div>
                <div className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-1 capitalize`}>
                  {stats?.plan_type || 'Starter'}
                </div>
                <p className={`text-sm ${isDark ? 'text-[#9BA3BF]' : 'text-slate-600'}`}>
                  {stats?.plan_type === 'starter' ? '15 consultas/mês' : 'Ilimitado'}
                </p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className={`${isDark ? 'bg-[#1A1D27] border-[#2A2E42]' : 'bg-white border-slate-200'} rounded-xl border shadow-sm`}>
              <div className={`px-6 py-5 border-b ${isDark ? 'border-[#2A2E42]' : 'border-slate-200'}`}>
                <h2 className={`text-xl font-semibold ${isDark ? 'text-[#F0F2FF]' : 'text-slate-900'}`}>Atividade Recente</h2>
              </div>
              <div className={`divide-y ${isDark ? 'divide-[#2A2E42]' : 'divide-slate-100'}`}>
                {stats?.recent_consultations && stats.recent_consultations.length > 0 ? (
                  <>
                    {stats.recent_consultations.map((consultation) => {
                      const statusBadge = getStatusBadge(consultation.status);
                      const typeBadge = getTypeBadge(consultation.consultation_type);
                      const { date, time } = formatDate(consultation.created_at);
                      const patientName = consultation.patient_name || 'Paciente sem identificação';
                      
                      return (
                        <Link
                          key={consultation.id}
                          to={`/consultations/${consultation.id}`}
                          className={`block px-6 py-4 ${isDark ? 'hover:bg-[#22263A]' : 'hover:bg-slate-50'} transition-colors`}
                          data-testid={`recent-consultation-${consultation.id}`}
                        >
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            {/* Left side: Type badge + Patient name */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* Type Badge */}
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${typeBadge.className}`}>
                                {typeBadge.label}
                              </span>
                              
                              {/* Patient Name */}
                              <span className={`font-medium truncate ${isDark ? 'text-[#F0F2FF]' : 'text-slate-900'}`}>
                                {patientName}
                              </span>
                            </div>
                            
                            {/* Right side: Date + Time + Status badge */}
                            <div className="flex items-center gap-3">
                              {/* Date */}
                              <span className={`text-sm ${isDark ? 'text-[#9BA3BF]' : 'text-slate-500'}`}>
                                {date}
                              </span>
                              
                              {/* Time */}
                              <span className={`text-sm font-medium ${isDark ? 'text-[#C8CEDE]' : 'text-slate-600'}`}>
                                {time}
                              </span>
                              
                              {/* Status Badge */}
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusBadge.className}`}>
                                {statusBadge.label}
                              </span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                    
                    {/* Ver todas as atividades link */}
                    <div className={`px-6 py-4 text-center ${isDark ? 'bg-[#1A1D27]' : 'bg-slate-50/50'}`}>
                      <Link 
                        to="/consultations" 
                        className={`text-sm font-medium ${isDark ? 'text-primary hover:text-primary-hover' : 'text-primary hover:text-primary-hover'} transition-colors`}
                      >
                        Ver todas as atividades →
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="px-6 py-12 text-center">
                    <FileText className={`h-12 w-12 ${isDark ? 'text-[#5C6480]' : 'text-slate-300'} mx-auto mb-4`} />
                    <p className={`${isDark ? 'text-[#9BA3BF]' : 'text-slate-600'} mb-2`}>
                      Nenhuma atividade registrada ainda.
                    </p>
                    <p className={`${isDark ? 'text-[#5C6480]' : 'text-slate-500'} text-sm mb-4`}>
                      Inicie uma nova consulta para começar.
                    </p>
                    <Link to="/consultations/new">
                      <Button className="bg-primary hover:bg-primary-hover">
                        Criar primeira consulta
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ONBOARD-01 — One-time upgrade prompt at 12/15 consultations (Starter only) */}
      <UpgradePromptModal
        open={upgradePromptOpen}
        onOpenChange={setUpgradePromptOpen}
        onAcknowledged={() => { refreshUser?.(); }}
      />

      {/* NPS-01 — Survey + referral (triggered after the 5th approval) */}
      <NPSSurveyModal
        open={npsOpen}
        onClose={() => setNpsOpen(false)}
      />
    </DashboardLayout>
  );
}
